import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { AuthContext } from 'core/components/auth/auth.context';
import { isCondensedLayout } from 'core/components/theme/condensed-layout';
import { buildViewAsSession, canUseViewAs } from './view-as-session';

export const ViewAsContext = createContext(null);

const NO_ENTERED_SUBJECT = { affinity: '', state: '', agencyName: null };

/**
 * View as (condensed layout only): lets a Service rep see the app as a typical agent at the agency
 * on the policy they're viewing. It re-provides AuthContext with a stand-in session, so every
 * `useSession()` gate follows it. Nothing else changes: Apollo, Amplify and search read the real
 * tokens and preferences, so records aren't filtered and actions still run as the rep.
 * In memory only, so a reload ends it.
 */
export function ViewAsProvider({ children }) {
  const realSession = useContext(AuthContext);
  const [agencyView, setAgencyView] = useState(false);
  const [principal, setPrincipal] = useState(false);
  const [unlicensed, setUnlicensed] = useState(false);
  // The policy the customer page is showing; outside a customer the rep enters one.
  const [pageSubject, setPageSubject] = useState(null);
  const [enteredSubject, setEnteredSubject] = useState(NO_ENTERED_SUBJECT);

  const available = isCondensedLayout() && canUseViewAs(realSession);
  const subject = useMemo(() => {
    const affinity = enteredSubject.affinity.trim();
    return pageSubject ?? (affinity && enteredSubject.state ? { ...enteredSubject, affinity } : null);
  }, [pageSubject, enteredSubject]);
  const active = available && agencyView && Boolean(subject);

  const session = useMemo(
    () => (active ? buildViewAsSession({ realSession, subject, principal, unlicensed }) : realSession),
    [active, realSession, subject, principal, unlicensed]
  );

  // Leaving the customer keeps their agency, now editable in the menu.
  const registerPageSubject = useCallback((nextSubject) => {
    setPageSubject(nextSubject);
    return () => {
      setPageSubject(null);
      setEnteredSubject(nextSubject);
    };
  }, []);

  const viewAs = useMemo(
    () => ({
      available,
      active,
      realSession,
      subject,
      hasPageSubject: Boolean(pageSubject),
      enteredSubject,
      principal,
      unlicensed,
      showAgencyView: () => setAgencyView(true),
      showBranchView: () => setAgencyView(false),
      setPrincipal,
      setUnlicensed,
      setEnteredSubject,
      registerPageSubject
    }),
    [available, active, realSession, subject, pageSubject, enteredSubject, principal, unlicensed, registerPageSubject]
  );

  // Always the same tree, so turning the lens on or off re-renders consumers instead of remounting them.
  return (
    <ViewAsContext.Provider value={viewAs}>
      <AuthContext.Provider value={session}>{children}</AuthContext.Provider>
    </ViewAsContext.Provider>
  );
}

ViewAsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useViewAs = () => useContext(ViewAsContext);

// The signed-in user's own session, for code that identifies them to a third party.
export const useRealSession = () => {
  const viewAs = useContext(ViewAsContext);
  const session = useContext(AuthContext);
  return viewAs?.realSession ?? session;
};

// The customer page names the policy whose agency the lens borrows.
export const useViewAsSubject = ({ affinity, state, agencyName }) => {
  const registerPageSubject = useContext(ViewAsContext)?.registerPageSubject;
  useEffect(() => {
    if (!registerPageSubject || !affinity || !state) {
      return undefined;
    }
    return registerPageSubject({ affinity, state, agencyName: agencyName ?? null });
  }, [registerPageSubject, affinity, state, agencyName]);
};
